import { isFamilyToken, ASSET_FAMILY } from '@exodus/assets';
import { captureStackTrace } from '@exodus/errors';
import { deserialize, createUnsignedTxFactory } from '@exodus/ethereum-lib';
import { Order } from '@exodus/models';
import { VersionedTransaction } from '@exodus/solana-web3.js';
import createFetchival from '@exodus/fetch/create-fetchival';
import { SwapMode } from '@exodus/exchange-client-providers';
import { memoize } from '@exodus/basic-utils';
import assert from 'minimalistic-assert';
import ms from 'ms';
import { ExchangeStatus, LOGS } from './constants.js';
import { notifyExchangeServerOfClientError } from './error-notification.js';
import { QuoteNotFoundError } from './errors.js';
const TX_TYPES = {
    PRE_SETUP: 'preSetupTransaction',
    SETUP: 'setupTransaction',
    SWAP: 'swapTransaction',
    CLEANUP: 'cleanupTransaction',
    DEPOSIT: 'depositTransaction',
    PAYOUT: 'payoutTransaction',
};
const getCreateUnsignedTx = memoize((chainId) => createUnsignedTxFactory({ chainId }));
export class Exchange {
    #analytics;
    #activeWalletAccountAtom;
    #addressProvider;
    #analyticsUserIdAtom;
    #config;
    #errorTracking;
    #exchangeAssets;
    #exchangeProviders;
    #exchangeQuotesAtom;
    #exchangeStatusAtom;
    #fetchival;
    #getBuildMetadata;
    #logger;
    #orders;
    #transactionSigner;
    #txSend;
    #walletAccountsAtom;
    #referralsAtom;
    #tracer;
    #exchangeTxSimulator;
    #exchangeFees;
    constructor(params) {
        this.#fetchival = createFetchival({ fetch: params.fetch });
        this.#activeWalletAccountAtom = params.activeWalletAccountAtom;
        this.#addressProvider = params.addressProvider;
        this.#analyticsUserIdAtom = params.analyticsUserIdAtom;
        this.#config = params.config;
        this.#errorTracking = params.unsafe__errorTracking;
        this.#exchangeAssets = params.exchangeAssets;
        this.#exchangeProviders = params.exchangeProviders;
        this.#exchangeQuotesAtom = params.exchangeQuotesAtom;
        this.#exchangeStatusAtom = params.exchangeStatusAtom;
        this.#getBuildMetadata = params.getBuildMetadata;
        this.#logger = params.logger;
        this.#orders = params.orders;
        this.#transactionSigner = params.transactionSigner;
        this.#txSend = params.txSend;
        this.#walletAccountsAtom = params.walletAccountsAtom;
        this.#referralsAtom = params.referralsAtom;
        this.#tracer = params.exchangeTracer;
        this.#analytics = params.analytics;
        this.#exchangeTxSimulator = params.exchangeTxSimulator;
        this.#exchangeFees = params.exchangeFees;
    }
    exchange = async (params) => {
        const { fromAssetName, exchangeButton, swapMode = SwapMode.ExactIn, toAssetName, fromWalletAccount: _fromWalletAccount, toWalletAccount: _toWalletAccount, toAddress: toAddressParam, slippage, isSendAll, } = params;
        this.#tracer?.clear();
        this.#tracer?.traceTime('TotalExchange');
        if (toAddressParam && _toWalletAccount) {
            throw new Error('Cannot specify both toAddress and toWalletAccount');
        }
        const activeWalletAccount = await this.#activeWalletAccountAtom.get();
        const fromWalletAccount = _fromWalletAccount || activeWalletAccount;
        const toWalletAccount = _toWalletAccount || activeWalletAccount;
        let executionSwapStrategy;
        let executionRateId;
        let executionQuoteId;
        let executionFromAmount;
        let executionToAmount;
        let isChainedOrder = false;
        let createdOrderId;
        try {
            const { quote } = params;
            if (!quote) {
                throw new QuoteNotFoundError({
                    message: 'Missing exchange quote',
                });
            }
            executionSwapStrategy = quote.swapStrategy;
            executionRateId = quote.rateId;
            executionQuoteId = quote.id;
            executionFromAmount = quote.fromAmount;
            executionToAmount = quote.toAmount;
            isChainedOrder = executionSwapStrategy === 'chained';
            await this.#exchangeStatusAtom.set({
                status: ExchangeStatus.STARTED,
                fromAssetName,
                toAssetName,
                fromAmount: executionFromAmount,
                toAmount: executionToAmount,
                error: null,
                orderId: null,
            });
            const fromAsset = this.#exchangeAssets.getAsset({ assetName: fromAssetName });
            const toAsset = this.#exchangeAssets.getAsset({ assetName: toAssetName });
            const walletAccounts = await this.#walletAccountsAtom.get();
            const fromWalletAccountInstance = walletAccounts[fromWalletAccount];
            assert(fromWalletAccountInstance, `Exchange: unknown from wallet account "${fromWalletAccount}"`);
            let fromAddress = await this.#addressProvider.getReceiveAddress({
                assetName: fromAsset.name,
                walletAccount: fromWalletAccountInstance,
            });
            const anonymousId = await this.#analyticsUserIdAtom?.get();
            const provider = this.#getProvider(executionSwapStrategy);
            const referrals = await this.#referralsAtom?.get();
            const referralCode = referrals?.referralCode;
            let fromAddressTag, toAddressTag;
            let toAddress;
            if (toAddressParam) {
                if (!toAsset.baseAsset.address.validate(toAddressParam)) {
                    throw new Error(`Invalid toAddress for ${toAsset.name}: ${toAddressParam}`);
                }
                toAddress = toAddressParam;
            }
            else {
                const toWalletAccountInstance = walletAccounts[toWalletAccount];
                assert(toWalletAccountInstance, `Exchange: unknown to wallet account "${toWalletAccount}"`);
                let derivedToAddress = await this.#addressProvider.getReceiveAddress({
                    assetName: toAsset.name,
                    walletAccount: toWalletAccountInstance,
                });
                if (toAsset.api.features?.abstractAccounts) {
                    this.#traceMessage(LOGS.GETTING_ACCOUNT_NAME);
                    const { accountCreator, accountCreatorMemo, mainAccountName } = await toAsset.api
                        .getAbstractAccountName({
                        walletAccount: toWalletAccount,
                    });
                    if (accountCreator) {
                        derivedToAddress = accountCreator;
                        toAddressTag = accountCreatorMemo;
                    }
                    else {
                        derivedToAddress = mainAccountName;
                    }
                }
                if (toAsset.ticker === 'ICP' && toAsset.address.displayAddress) {
                    derivedToAddress = toAsset.address.displayAddress(derivedToAddress.toString());
                }
                toAddress = derivedToAddress.toString();
            }
            if (fromAsset.ticker === 'ICP' && fromAsset.address.displayAddress) {
                fromAddress = fromAsset.address.displayAddress(fromAddress.toString());
            }
            const { transactions, providerOrderId, svc, orderId } = await this.#exchangeProviders.exchange({
                provider,
                fromAmount: executionFromAmount,
                fromAddress: fromAddress.toString(),
                fromAddressTag,
                toAmount: executionToAmount,
                pairId: `${fromAsset.ticker}_${toAsset.ticker}`,
                toAddress,
                toAddressTag,
                slippage,
                isSendAll,
                anonymousId,
                referralCode,
                swapMode,
                quote,
                simulateTx: (simulationTxData) => {
                    this.#traceMessage(LOGS.SIMULATING_TX);
                    return this.#exchangeTxSimulator.simulateTx({
                        ...simulationTxData,
                        walletAccount: fromWalletAccount,
                    });
                },
                signAndSendTx: (args) => this.#signAndSendTx({ walletAccount: fromWalletAccount, ...args }),
                ...{
                    signTx: (args) => this.#signTx({ ...args, walletAccount: fromWalletAccount }),
                },
                createAndSendTx: (args) => this.#createAndSendTx({
                    walletAccount: fromWalletAccount,
                    fromAddress: fromAddress.toString(),
                    ...args,
                }),
                createTxBundle: (args) => this.#createTxBundle({ walletAccount: fromWalletAccount, ...args }),
                createAndSendEvmTx: (args) => this.#createAndSendEvmTx({ walletAccount: fromWalletAccount, ...args }),
                sendTxBundle: (args) => this.#sendTxBundle(args),
            });
            createdOrderId = orderId;
            await this.#exchangeStatusAtom.set((prev) => {
                if (prev)
                    return { ...prev, orderId: providerOrderId };
                return prev;
            });
            void this.#postProcessExchange({ toAsset, toWalletAccount });
            try {
                const fromTx = transactions.find((tx) => [TX_TYPES.SWAP, TX_TYPES.DEPOSIT].includes(tx.type));
                const fromTxId = fromTx?.id || transactions[0]?.id;
                const orderData = {
                    orderId: providerOrderId,
                    txIds: transactions.map((tx) => ({ type: tx.type, txId: tx.id })),
                    fromAmount: executionFromAmount,
                    fromAsset: fromAssetName,
                    toAmount: executionToAmount,
                    toAsset: toAssetName,
                    fromTxId,
                    toTxId: undefined,
                    fromWalletAccount,
                    toWalletAccount,
                    toAddress: toAddressParam,
                    svc,
                    slippage,
                };
                this.#traceMessage(LOGS.UPSERTING_ORDER);
                this.#orders.upsert([Order.fromJSON(orderData)]).catch((err) => {
                    this.#traceMessage(LOGS.FAILED_TO_UPSERT_ORDER);
                    throw err;
                });
                return orderData.orderId;
            }
            catch (err) {
                if (createdOrderId) {
                    void this.#logExchangeError({ orderId: createdOrderId, error: err, isChainedOrder });
                }
            }
            finally {
                await this.#exchangeStatusAtom.set({
                    status: ExchangeStatus.IN_PROGRESS,
                    fromAssetName,
                    toAssetName,
                    fromAmount: executionFromAmount,
                    toAmount: executionToAmount,
                    orderId: providerOrderId,
                    error: null,
                });
            }
        }
        catch (err) {
            captureStackTrace(err);
            if (err.orderId) {
                createdOrderId = err.orderId;
            }
            if (createdOrderId) {
                void this.#logExchangeError({
                    orderId: createdOrderId,
                    error: err,
                    isChainedOrder,
                });
            }
            await this.#exchangeStatusAtom.set({
                status: ExchangeStatus.FAILED,
                fromAssetName,
                toAssetName,
                fromAmount: executionFromAmount,
                toAmount: executionToAmount,
                orderId: createdOrderId || null,
                error: err,
            });
            throw err;
        }
        finally {
            if (createdOrderId) {
                const _exchangeButton = exchangeButton === 'min' ? 'm' : exchangeButton === 'half' ? 'h' : exchangeButton;
                const walletAccounts = await this.#walletAccountsAtom.get();
                const [fromSource, toSource] = [fromWalletAccount, toWalletAccount].map((walletAccount) => {
                    const walletAccountInstance = walletAccounts[walletAccount];
                    if (!walletAccountInstance) {
                        throw new Error(`Exchange: wallet account not found for metadata (key: "${walletAccount}")`);
                    }
                    const { source, isHardware } = walletAccountInstance;
                    if (isHardware)
                        return source;
                    return 'exodus';
                });
                this.#logExchangeMetadata({
                    orderId: createdOrderId,
                    buttonPressed: _exchangeButton,
                    rateId: executionRateId,
                    fromSource,
                    toSource,
                    isChainedOrder,
                }).catch((err) => this.#logger.error(`Failed to log metadata: ${err.message || err.toString()}`));
            }
            if (executionQuoteId) {
                const quoteIdToClear = executionQuoteId;
                await this.#exchangeQuotesAtom.set((prev) => ({ ...prev, [quoteIdToClear]: null }));
            }
            this.#tracer?.traceTimeEnd('TotalExchange');
            if (executionSwapStrategy) {
                void this.#trackExchangeTimes({
                    swapStrategy: executionSwapStrategy,
                    orderId: createdOrderId,
                });
            }
        }
    };
    ackExchange = () => {
        this.#tracer?.clear();
        void this.#exchangeStatusAtom.set(null);
    };
    #postProcessExchange = async (params) => {
        try {
            const { toAsset, toWalletAccount } = params;
            if (toAsset.api.features?.abstractAccounts) {
                const { accountCreator } = await toAsset.api.getAbstractAccountName({
                    walletAccount: toWalletAccount,
                    createNewAccount: false,
                });
                const isNewAccount = Boolean(accountCreator);
                if (isNewAccount && typeof toAsset.startAccountDiscovery === 'function') {
                    await toAsset.startAccountDiscovery({
                        walletAccount: toWalletAccount,
                        duration: ms('1d'),
                    });
                }
            }
        }
        catch (err) {
            this.#logger.error('Failed to post process exchange', err);
            this.#traceMessage(LOGS.START_ACCOUNT_DISCOVERY_FAILED);
        }
    };
    #getProvider = (swapStrategy) => {
        const provider = this.#exchangeProviders.getProviderByStrategy(swapStrategy);
        if (!provider) {
            throw new Error(`Unknown provider: ${swapStrategy}`);
        }
        return provider;
    };
    #logExchangeError = async ({ orderId, error: rawError, isChainedOrder, }) => {
        const error = rawError instanceof Error ? rawError : new Error(String(rawError));
        captureStackTrace(error);
        this.#tracer?.traceError(error);
        const trace = this.#tracer?.print();
        if (trace) {
            this.#logger.error(`exchange error trace:\n${trace.slice(0, 3000)}`);
        }
        const safeMessages = this.#tracer?.getSafeMessages();
        this.#errorTracking
            ?.track({
            error,
            context: safeMessages?.length ? { exchange: { events: safeMessages } } : undefined,
        })
            .catch((trackingError) => this.#logger.error('exchange error tracking failed', trackingError));
        await notifyExchangeServerOfClientError(this.#request, {
            orderId,
            isChainedOrder,
            logger: this.#logger,
        });
    };
    #trackExchangeTimes = async (params) => {
        const times = this.#tracer?.timeLogs;
        if (times && this.#analytics) {
            void this.#analytics.track({
                event: 'ExchangeTimeTrace',
                properties: {
                    ...Object.fromEntries(times.entries()),
                    ProviderName: params.swapStrategy,
                    OrderId: params.orderId || 'N/A',
                },
            });
        }
    };
    #logExchangeMetadata = async ({ orderId, buttonPressed, rateId, fromSource, toSource, isChainedOrder, }) => {
        const buildMetadata = await this.#getBuildMetadata();
        const { build: clientBuild, dev: isDev, osName: clientOs, osVersion: clientOsVersion, } = buildMetadata;
        const endpoint = isChainedOrder ? 'chained-orders' : 'orders';
        return this.#request(`${endpoint}/${encodeURIComponent(orderId)}/metadata`).post({
            buttonPressed,
            clientBuild,
            clientOs,
            clientOsVersion,
            isDev,
            rateId,
            fromSource,
            toSource,
        });
    };
    #request = (path) => {
        const { appPlatform } = this.#config.providers.direct;
        return this.#fetchival(new URL(`${this.#config.providers.direct.baseUrl}/${path}`), {
            headers: {
                'App-Name': this.#config.providers.direct.appName,
                'App-Version': this.#config.providers.direct.appVersion,
                ...(appPlatform ? { 'App-Platform': appPlatform } : {}),
            },
        });
    };
    #createCexTx = async ({ asset, recipient, amount, walletAccount, opts, isSendAll, feeOpts, fromAddress, quote, }) => {
        let txInput;
        const isBaseAsset = asset.baseAsset.name === asset.name;
        if (opts?.pidBytes) {
            const _txInput = isBaseAsset
                ? []
                : isFamilyToken({ asset, family: ASSET_FAMILY.EVM })
                    ? asset.contract.transfer.build(recipient.toLowerCase(), amount.toBaseString({ unit: false }))
                    : [];
            txInput = Buffer.from(new Uint8Array([..._txInput, ...opts.pidBytes]));
        }
        const memo = opts?.payInAddressTag;
        let deltaIncrease = asset.feeAsset.currency.ZERO;
        try {
            const freshFeeOpts = await this.#exchangeFees.getFeeOpts({
                assetId: asset.name,
                walletAccount,
                fromAddress,
                toAddress: recipient,
                amount,
                ...(txInput ? { txInput } : {}),
                ...(memo ? { memo } : {}),
                isExchange: true,
            });
            const oldFee = feeOpts.fee;
            const newFee = freshFeeOpts.fee;
            if (oldFee && newFee.gt(oldFee)) {
                deltaIncrease = newFee.sub(oldFee);
            }
            feeOpts = { ...feeOpts, ...freshFeeOpts };
        }
        catch {
            console.warn(`Failed to get fresh fees for ${asset.name}, keeping quote fees as fallback`);
        }
        const feeAmount = Object.hasOwn(feeOpts, 'fee') ? feeOpts.fee : asset.feeAsset.currency.ZERO;
        const isFeeAsset = asset.feeAsset.name === asset.name;
        if (isFeeAsset && isSendAll) {
            const adjustedAmount = amount.sub(deltaIncrease);
            if (quote?.min && adjustedAmount.lt(quote.min)) {
                throw new Error(`Adjusted amount ${adjustedAmount.toDefaultString()} is below minimum exchange amount ${quote.min.toDefaultString()} for ${asset.name}`);
            }
            amount = adjustedAmount;
        }
        const { unsignedTx: _unsignedTx, ...sendFeeOpts } = feeOpts;
        return {
            ...sendFeeOpts,
            ...(txInput ? { txInput, keepTxInput: true } : {}),
            assetName: asset.name,
            address: recipient,
            amount,
            walletAccount,
            isSendAll: Boolean(isSendAll),
            isRbfAllowed: false,
            isExchange: true,
            feeAmount,
            ...(memo ? { memo } : {}),
        };
    };
    #createTx = async ({ amount, assetId, recipient, opts, isSendAll, walletAccount, feeOpts, fromAddress, quote, }) => {
        try {
            const asset = this.#exchangeAssets.getAsset({ assetId });
            return await this.#createCexTx({
                asset,
                recipient: recipient,
                amount: amount,
                walletAccount,
                opts,
                isSendAll,
                feeOpts: feeOpts,
                fromAddress: fromAddress,
                quote,
            });
        }
        catch (err) {
            this.#logger.error('Failed to create tx', err);
            throw err;
        }
    };
    #createAndSendTx = async (tx) => {
        try {
            const txData = await this.#createTx(tx);
            return await this.#txSend(txData);
        }
        catch (err) {
            this.#logger.error('Failed to send tx', err);
            throw err;
        }
    };
    #createAndSendEvmTx = async ({ txInput, amount, gasLimit, gasPrice, tipGasPrice, assetId, fromAddress, contractAddress, walletAccount, txValue, }) => {
        try {
            const asset = this.#exchangeAssets.getAsset({ assetId });
            const baseAsset = asset.baseAsset;
            if (!baseAsset.api.sendTx) {
                throw new Error('Expected EVM base asset with sendTx method');
            }
            if (!baseAsset.api.createTx) {
                throw new Error('Expected EVM base asset with createTx method');
            }
            const walletAccounts = await this.#walletAccountsAtom.get();
            const walletAccountInstance = walletAccounts[walletAccount];
            assert(walletAccountInstance, `Exchange: unknown wallet account "${walletAccount}" (EVM createAndSendEvmTx)`);
            const txValueAmount = txValue ? asset.feeAsset.currency.baseUnit(txValue) : undefined;
            const { unsignedTx } = await baseAsset.api.createTx({
                asset,
                fromAddress,
                amount,
                txInput,
                gasLimit,
                gasPrice,
                tipGasPrice,
                toAddress: contractAddress,
                walletAccount,
                txValue: txValueAmount,
            });
            return await baseAsset.api.sendTx({
                asset,
                walletAccount,
                unsignedTx,
            });
        }
        catch (err) {
            this.#logger.error('Failed to send tx', err);
            throw err;
        }
    };
    #signAndSendTx = async ({ assetId, rawTx: unsignedRawTx, walletAccount, amount, fee, sender, recipient, }) => {
        const asset = this.#exchangeAssets.getAsset({ assetId });
        const serializedTxBuffer = Buffer.from(unsignedRawTx, 'base64');
        const walletAccounts = await this.#walletAccountsAtom.get();
        const walletAccountInstance = walletAccounts[walletAccount];
        assert(walletAccountInstance, `Exchange: unknown wallet account "${walletAccount}" (Solana signAndSendTx)`);
        assert(Number.isInteger(walletAccountInstance.index), 'expected walletAccount index');
        const baseAssetApi = asset.baseAsset.api;
        assert(baseAssetApi.sendTx, 'Expected Solana base asset with sendTx method');
        const unsignedTx = {
            txData: {
                transaction: VersionedTransaction.deserialize(serializedTxBuffer),
                transactionBuffer: serializedTxBuffer,
                method: 'swap',
                ...(amount !== undefined && { amount: amount.toBaseString({ unit: false }) }),
                ...(sender !== undefined && { from: sender }),
                ...(recipient !== undefined && { to: recipient }),
            },
            txMeta: {
                assetName: asset.baseAsset.name,
                accountIndex: walletAccountInstance.index,
                preferExplicitTxLogFields: true,
                ...(fee !== undefined && { fee }),
            },
        };
        return baseAssetApi.sendTx({
            asset,
            walletAccount: walletAccountInstance,
            unsignedTx,
        });
    };
    #signTx = async ({ assetId, rawTx: unsignedRawTx, walletAccount, }) => {
        const asset = this.#exchangeAssets.getAsset({ assetId });
        const serializedTxBuffer = Buffer.from(unsignedRawTx, 'base64');
        const walletAccounts = await this.#walletAccountsAtom.get();
        const walletAccountInstance = walletAccounts[walletAccount];
        assert(walletAccountInstance, `Exchange: unknown wallet account "${walletAccount}" (Solana signTx)`);
        const unsignedTx = {
            txData: {
                transaction: VersionedTransaction.deserialize(serializedTxBuffer),
                transactionBuffer: serializedTxBuffer,
            },
            txMeta: {
                assetName: asset.baseAsset.name,
                accountIndex: walletAccountInstance.index,
            },
        };
        const { rawTx } = await this.#transactionSigner.signTransaction({
            baseAssetName: asset.baseAsset.name,
            unsignedTx,
            walletAccount: walletAccountInstance,
        });
        const signedTx = typeof rawTx === 'string' ? rawTx : rawTx.toString('base64');
        return { signedTx };
    };
    #createTxBundle = async ({ assetId, orderId, transactions, walletAccount, }) => {
        try {
            if (!Array.isArray(transactions) || transactions.length === 0)
                return null;
            if (transactions.some((e) => e.type !== 'evm'))
                return null;
            const asset = this.#exchangeAssets.getAsset({ assetId });
            const baseAsset = asset.baseAsset;
            if (typeof baseAsset.getNonce !== 'function')
                return null;
            if (typeof baseAsset.broadcastPrivateBundle !== 'function')
                return null;
            if (typeof baseAsset.broadcastPrivateTx !== 'function')
                return null;
            const walletAccounts = await this.#walletAccountsAtom.get();
            const walletAccountInstance = walletAccounts[walletAccount];
            assert(walletAccountInstance, `Exchange: unknown wallet account "${walletAccount}" (createTxBundle)`);
            const fromAddress = await this.#addressProvider.getReceiveAddress({
                assetName: asset.name,
                walletAccount: walletAccountInstance,
            });
            const baseNonce = await baseAsset.getNonce({
                asset,
                fromAddress: fromAddress.toString(),
                walletAccount: walletAccountInstance,
                tag: 'latest',
                forceFromNode: true,
            });
            const evmTransactionActionToType = {
                revoke: 'preSetupTransaction',
                approve: 'setupTransaction',
                swap: 'swapTransaction',
            };
            return await Promise.all(transactions
                .map((e, i) => [e, baseNonce + i])
                .map(async ([{ action, to: toAddress, data: txInput, gasLimit, gasPrice, tipGasPrice, value }, nonce,]) => {
                const type = evmTransactionActionToType[action];
                assert(type, 'unable to resolve type');
                assert(gasPrice, 'expected gasPrice');
                if (tipGasPrice && tipGasPrice.gt(gasPrice))
                    gasPrice = tipGasPrice;
                const createUnsignedTx = getCreateUnsignedTx(baseAsset.chainId);
                const unsignedTx = await createUnsignedTx({
                    asset: baseAsset,
                    address: toAddress,
                    amount: isFamilyToken({ asset, family: ASSET_FAMILY.EVM })
                        ? baseAsset.currency.ZERO
                        : baseAsset.currency.baseUnit(value),
                    nonce,
                    txInput,
                    gasLimit,
                    gasPrice,
                    tipGasPrice,
                    fromAddress: fromAddress.toString(),
                    eip1559Enabled: Boolean(tipGasPrice),
                    txValue: value ? asset.feeAsset.currency.baseUnit(value) : undefined,
                });
                assert(unsignedTx, 'expected unsignedTx');
                const { rawTx } = await this.#transactionSigner.signTransaction({
                    baseAssetName: asset.baseAsset.name,
                    unsignedTx,
                    walletAccount: walletAccountInstance,
                });
                assert(rawTx, 'expected rawTx');
                const deserialized = deserialize(`0x${rawTx.toString('hex')}`);
                const txId = `0x${deserialized.hash().toString('hex')}`;
                return { type, rawTx, txId };
            }));
        }
        catch (err) {
            void this.#logExchangeError({ orderId, error: err, isChainedOrder: false });
            return null;
        }
    };
    #sendTxBundle = async ({ assetId, txBundle, }) => {
        const asset = this.#exchangeAssets.getAsset({ assetId });
        const baseAsset = asset.baseAsset;
        assert(txBundle.length > 0, 'expected at least one transaction');
        const { broadcastPrivateBundle, broadcastPrivateTx } = baseAsset;
        if (txBundle.length === 1) {
            assert(typeof broadcastPrivateTx === 'function', 'expected broadcastPrivateTx');
            await Promise.all(txBundle.map(({ rawTx }) => broadcastPrivateTx(`0x${rawTx.toString('hex')}`)));
        }
        else {
            assert(typeof broadcastPrivateBundle === 'function', 'expected broadcastPrivateBundle');
            await broadcastPrivateBundle({
                txs: txBundle.map(({ rawTx }) => `0x${rawTx.toString('hex')}`),
            });
        }
        return txBundle.map(({ txId: id, type }) => ({ id, type }));
    };
    #traceMessage(message) {
        this.#tracer?.traceMessage(message);
    }
}
const exchangeFactory = (args) => new Exchange({ ...args });
const exchangeModuleDefinition = {
    id: 'exchange',
    type: 'module',
    factory: exchangeFactory,
    dependencies: [
        'analytics?',
        'activeWalletAccountAtom',
        'addressProvider',
        'analyticsUserIdAtom?',
        'config',
        'unsafe__errorTracking?',
        'exchangeAssets',
        'exchangeProviders',
        'exchangeQuotesAtom',
        'exchangeRemoteConfigAtom',
        'exchangeStatusAtom',
        'fetch',
        'getBuildMetadata',
        'logger',
        'orders',
        'referralsAtom?',
        'transactionSigner',
        'txSend',
        'walletAccountsAtom',
        'exchangeTxSimulator',
        'exchangeTracer?',
        'exchangeFees',
    ],
};
export default exchangeModuleDefinition;
